import { useQueries } from '@tanstack/react-query';
import { T, Var } from 'gt-react';
import { SplitHorizontalIcon } from '@phosphor-icons/react';
import type { SystemEntity } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { SystemEntityInline } from '@/components/system-entities';
import type { ConfirmedSplitPart } from './confirmedSplitMatch';
import { PartKindLabel } from './PartKindLabel';

interface SplitMatchCellProps {
  parts: readonly ConfirmedSplitPart[];
  /** Formats an amount in the invoice's currency. */
  money: (amount: string) => string;
}

/**
 * The match cell of an invoice line whose split AP accepted: one row per part
 * with its amount, what it is and the document it sits on. The ordinary cell
 * shows one charge, which would hide the other parts of the line.
 */
export function SplitMatchCell({ parts, money }: SplitMatchCellProps) {
  const documents = useQueries({
    queries: parts.map((part) =>
      orpc.finance.charges.getById.queryOptions({
        input: { id: part.chargeId },
      }),
    ),
    combine: (results) =>
      results.map((result) =>
        result.data?.charge
          ? {
              entityType: result.data.charge.docType as SystemEntity,
              entityId: result.data.charge.docId,
            }
          : null,
      ),
  });

  return (
    <div className="space-y-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">
      <div className="flex items-center gap-1.5 font-medium">
        <SplitHorizontalIcon className="h-3.5 w-3.5 shrink-0" />
        <T context="heading of the match cell of an AP invoice line whose split was accepted">
          Split into <Var>{parts.length}</Var> parts
        </T>
      </div>
      <ul className="space-y-0.5">
        {parts.map((part, index) => {
          const document = documents[index];
          return (
            <li
              key={part.chargeId}
              className="flex flex-wrap items-center gap-x-1.5"
            >
              <span className="font-medium tabular-nums">
                {money(part.amount)}
              </span>
              <PartKindLabel kind={part.kind} />
              {document && (
                <SystemEntityInline
                  entityType={document.entityType}
                  entityId={document.entityId}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
